import { createVersionedSessionDraftStore } from "@/app/booking/core/versionedSessionDraftStore";
import { quarantineCustomerOwnedSessionStorage } from "@/app/booking/core/customerOwnedSessionStorageBarrier";

import {
  validateAppointmentWaitlistReceipt,
  type AppointmentWaitlistReceiptV3,
} from "./appointmentWaitlistReceipt";
import { migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage } from "./retiredGroupAppointmentWaitlistStorageMigration";
import { APPOINTMENT_WAITLIST_RECEIPT_STORAGE_MAX_SERIALIZED_CHARACTERS } from "./appointmentWaitlistStorageLimits";

export const APPOINTMENT_WAITLIST_RECEIPT_STORAGE_KEY =
  "volera:appointment-waitlist-receipts:v3";
export const LEGACY_APPOINTMENT_WAITLIST_RECEIPT_V2_STORAGE_KEY =
  "volera:appointment-waitlist-receipts:v2";
export const LEGACY_APPOINTMENT_WAITLIST_RECEIPT_V1_STORAGE_KEY =
  "volera:appointment-waitlist-receipts:v1";
const appointmentWaitlistReceiptStore =
  createVersionedSessionDraftStore<AppointmentWaitlistReceiptV3>({
    storageKey: APPOINTMENT_WAITLIST_RECEIPT_STORAGE_KEY,
    storeKind: "appointment-waitlist-receipts",
    maxEntries: 8,
    maxAgeMs: 24 * 60 * 60 * 1_000,
    maxSerializedCharacters:
      APPOINTMENT_WAITLIST_RECEIPT_STORAGE_MAX_SERIALIZED_CHARACTERS,
    beforeAccess:
      migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage,
    validate: (value) => {
      const validation = validateAppointmentWaitlistReceipt(value);

      return validation.valid
        ? { valid: true as const, draft: validation.receipt }
        : validation;
    },
  });

export function storeAppointmentWaitlistReceipt(value: unknown) {
  const validation = validateAppointmentWaitlistReceipt(value);

  if (!validation.valid) {
    return null;
  }

  const receipt = validation.receipt;
  const existing = appointmentWaitlistReceiptStore.readDraft(receipt.draftId);

  if (existing) {
    return JSON.stringify(existing) === JSON.stringify(receipt)
      ? existing
      : null;
  }

  return appointmentWaitlistReceiptStore.storeDraftPersisted(receipt);
}

export function readAppointmentWaitlistReceipt(
  draftId: string,
  customerAccountId: string,
) {
  let inspection: ReturnType<
    typeof appointmentWaitlistReceiptStore.inspectPersistedDraft
  >;
  try {
    inspection = appointmentWaitlistReceiptStore.inspectPersistedDraft(draftId);
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return null;
  }
  if (inspection.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
    return null;
  }
  const receipt = inspection.status === "present" ? inspection.draft : null;

  return receipt?.customerAccountId === customerAccountId ? receipt : null;
}

export function inspectAppointmentWaitlistReceiptsPersisted() {
  return appointmentWaitlistReceiptStore.inspectPersistedDrafts();
}

export function inspectAppointmentWaitlistReceiptPersisted(
  draftId: string,
  evaluatedAtEpochMs?: number,
) {
  return appointmentWaitlistReceiptStore.inspectPersistedDraft(
    draftId,
    evaluatedAtEpochMs,
  );
}

export function createAppointmentWaitlistReceiptStrictPersisted(
  value: unknown,
) {
  return appointmentWaitlistReceiptStore.createDraftStrictPersisted(value);
}

export function removeAppointmentWaitlistReceiptStrictPersisted(
  value: unknown,
) {
  return appointmentWaitlistReceiptStore.removeDraftStrictPersisted(value);
}

export function clearAppointmentWaitlistReceipts() {
  const currentCleared = appointmentWaitlistReceiptStore.clearDrafts();
  let legacyCleared = false;

  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.removeItem(
        LEGACY_APPOINTMENT_WAITLIST_RECEIPT_V1_STORAGE_KEY,
      );
      window.sessionStorage.removeItem(
        LEGACY_APPOINTMENT_WAITLIST_RECEIPT_V2_STORAGE_KEY,
      );
      legacyCleared =
        window.sessionStorage.getItem(
          LEGACY_APPOINTMENT_WAITLIST_RECEIPT_V1_STORAGE_KEY,
        ) === null &&
        window.sessionStorage.getItem(
          LEGACY_APPOINTMENT_WAITLIST_RECEIPT_V2_STORAGE_KEY,
        ) === null;
    } catch {
      legacyCleared = false;
    }
  }

  return currentCleared && legacyCleared;
}
